import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  TextField,
  Box,
  Typography,
  Chip,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  TextSnippet as TemplateIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Send as SendIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Mood as MoodIcon,
  Computer as PlatformIcon,
} from '@mui/icons-material';
import { MessageTemplate, messageTemplatesAPI, templateHelpers } from '../../services/api/messageTemplates.service';

interface TemplatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: MessageTemplate, processedContent: string, variables: Record<string, string>) => void;
  threadId: string;
  brandId: string;
  customerUsername?: string;
  suggestedTemplates?: MessageTemplate[];
  loading?: boolean;
}

const TemplatePickerDialog: React.FC<TemplatePickerDialogProps> = ({
  open,
  onClose,
  onSelectTemplate,
  threadId,
  brandId,
  customerUsername,
  suggestedTemplates,
  loading = false
}) => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateType, setTemplateType] = useState<string>('reply');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [processedContent, setProcessedContent] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load templates when dialog opens
  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open, brandId, templateType]);

  // Use suggested templates if provided
  useEffect(() => {
    if (suggestedTemplates && suggestedTemplates.length > 0) {
      setTemplates(suggestedTemplates);
      setFilteredTemplates(suggestedTemplates);
    }
  }, [suggestedTemplates]);

  // Filter templates based on search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredTemplates(templates);
    } else {
      const filtered = templates.filter(template =>
        template.template_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.template_content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTemplates(filtered);
    }
  }, [templates, searchQuery]);

  // Process template when selected or variables change
  useEffect(() => {
    if (selectedTemplate) {
      processTemplate(selectedTemplate);
    }
  }, [selectedTemplate, variables]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    setError(null);
    try {
      const response = await messageTemplatesAPI.getTemplates(brandId, {
        template_type: templateType,
        platform: 'instagram',
        is_active: true,
        limit: 50
      });
      setTemplates(response.data);
      setFilteredTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const processTemplate = (template: MessageTemplate) => {
    const templateVariables = templateHelpers.extractVariables(template.template_content);
    
    // Auto-fill common variables
    const autoFilledVariables: Record<string, string> = {};
    templateVariables.forEach(variable => {
      switch (variable.toLowerCase()) {
        case 'customer_name':
          autoFilledVariables[variable] = customerUsername || 'Customer';
          break;
        case 'brand_name':
          autoFilledVariables[variable] = template.brand_name || 'Brand';
          break;
        default:
          autoFilledVariables[variable] = variables[variable] || '';
      }
    });

    setVariables(autoFilledVariables);
    
    // Process the template content
    const processed = templateHelpers.replaceVariables(template.template_content, autoFilledVariables);
    setProcessedContent(processed);
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setSelectedTemplate(template);
  };

  const handleVariableChange = (variable: string, value: string) => {
    setVariables(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate, processedContent, variables);
      onClose();
    }
  };

  const handleCopyTemplate = async () => {
    if (processedContent) {
      try {
        await navigator.clipboard.writeText(processedContent);
      } catch (error) {
        console.error('Failed to copy template:', error);
      }
    }
  };

  const renderTemplatesList = () => {
    if (templatesLoading) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (filteredTemplates.length === 0) {
      return (
        <Box p={2} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            No templates found
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {filteredTemplates.map((template) => (
          <ListItem key={template.id} disablePadding>
            <ListItemButton
              selected={selectedTemplate?.id === template.id}
              onClick={() => handleTemplateSelect(template)}
            >
              <ListItemIcon>
                <TemplateIcon color={template.is_default ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2">
                      {template.template_name}
                    </Typography>
                    {template.is_default && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {template.template_content}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5}>
                      {template.category && (
                        <Chip
                          label={template.category}
                          size="small"
                          variant="outlined"
                          icon={<CategoryIcon />}
                        />
                      )}
                      {template.sentiment && (
                        <Chip
                          label={template.sentiment}
                          size="small"
                          variant="outlined"
                          icon={<MoodIcon />}
                        />
                      )}
                      <Chip
                        label={`Used ${template.usage_count} times`}
                        size="small"
                        variant="outlined"
                        icon={<TrendingUpIcon />}
                      />
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderTemplatePreview = () => {
    if (!selectedTemplate) {
      return (
        <Box p={2} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Select a template to preview
          </Typography>
        </Box>
      );
    }

    const templateVariables = templateHelpers.extractVariables(selectedTemplate.template_content);

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {selectedTemplate.template_name}
          </Typography>
          
          {selectedTemplate.description && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedTemplate.description}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {templateVariables.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Template Variables:
              </Typography>
              {templateVariables.map((variable) => (
                <TextField
                  key={variable}
                  label={variable}
                  value={variables[variable] || ''}
                  onChange={(e) => handleVariableChange(variable, e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Preview:
          </Typography>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {processedContent}
              </Typography>
            </CardContent>
          </Card>
        </CardContent>
        <CardActions>
          <Button
            startIcon={<FileCopyIcon />}
            onClick={handleCopyTemplate}
            size="small"
          >
            Copy
          </Button>
          <Button
            startIcon={<SendIcon />}
            onClick={handleUseTemplate}
            variant="contained"
            size="small"
          >
            Use Template
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Message Templates</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" gap={2} mb={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
              label="Type"
            >
              <MenuItem value="reply">Reply</MenuItem>
              <MenuItem value="greeting">Greeting</MenuItem>
              <MenuItem value="follow_up">Follow Up</MenuItem>
              <MenuItem value="closing">Closing</MenuItem>
            </Select>
          </FormControl>

          <TextField
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display="flex" gap={2} height="calc(100% - 80px)">
          <Box flex={1} overflow="auto">
            <Typography variant="subtitle2" gutterBottom>
              Available Templates
            </Typography>
            {renderTemplatesList()}
          </Box>

          <Box flex={1} overflow="auto">
            <Typography variant="subtitle2" gutterBottom>
              Template Preview
            </Typography>
            {renderTemplatePreview()}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleUseTemplate}
          variant="contained"
          disabled={!selectedTemplate}
          startIcon={<SendIcon />}
        >
          Use Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatePickerDialog;